import { createReviewedWriter, applyReviewedChange } from '@/lib/canvas/reviewedChange'
import { makeChoiceWriters } from '@/lib/choices/applyDeps'
import {resolveConversationAction} from '@/lib/conversationActions';
import { connectedDestination, defaultPlanningDestination } from '@/lib/planning/connectedDestination';
import { useCadenceConfig } from '@/lib/cadence/config';
import { useGoals } from '@/hooks/useGoals';
import { useAssigneeFilter } from '@/hooks/useAssigneeFilter';
import { planPeopleLens } from '@/lib/planning/peopleLens';
import { workspaceContext, workspaceDestination } from '@/lib/workspace/context';
import { useWorkspaceVoiceAccess } from '@/hooks/useWorkspaceVoiceAccess';
import { useWorkspaceVoice } from '@/hooks/useWorkspaceVoice';
import './connected-workspace.css';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { assistantSelection } from './assistantSelection';
import { PlanNavigation, planPeriodForPath, MobilePlanControlsContext } from '@/components/layout/PlanNavigation';
import { PhoneBack } from '@/components/layout/BackButton';
import { requestPlanFromPaper } from '@/lib/planFromPaperSignal';
import { DesktopNavigation, DesktopControlsContext, DesktopLeadContext, DesktopCenterContext } from '@/components/layout/DesktopNavigation';
import { ReferenceListsProvider, useReferenceLists } from '@/components/reference/ReferenceListsContext';
import { ReferenceListsDock } from '@/components/reference/ReferenceLists';
import { pinIsOnPage } from '@/components/reference/periodsOnPage';
// src/shell/ShellLayout.tsx
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, MoreHorizontal, Plus, Sun, Map, Home as HomeIcon } from 'lucide-react';
import { HOME_ROUTE } from '@/apps/overview/route';
import { useTextEntryActive } from '@/hooks/useKeyboardInset';
import { type ViewType } from '@/components/layout/Sidebar';
import { MoreSheet } from '@/components/layout/MoreSheet';
import { QuickCapture } from '@/components/layout/QuickCapture';
import { NewVersionBanner } from '@/components/layout/NewVersionBanner';
import { OmniboxResults } from '@/components/omnibox/OmniboxResults';
import { DomainSwitcher } from '@/components/domain/DomainSwitcher';
import { HeaderPeopleFilter } from '@/components/layout/HeaderPeopleFilter';
import { Toast, ConfirmationToast, ToastLiveRegion } from '@/components/toast';
import { plannedWeekStart } from '@/lib/canvas/applyChangeSet';
import { withSteps, type ChangeSet } from '@/lib/canvas/changeCard';
import { useActionableInstances } from '@/hooks/useActionableInstances';
import { readCadenceConfig } from '@/lib/cadence/config';
import { NotesProvider } from '@/contexts/NotesContext';
import { ListsProvider } from '@/contexts/ListsContext';
import { PinsProvider } from '@/contexts/PinsContext';
import { useHouseholdWeekStart } from '@/hooks/useHouseholdWeekStart';
import { useAuth } from '@/hooks/useAuth';
import { setCurrentAccount } from '@/lib/currentAccount';
import { useMobile } from '@/hooks/useMobile';
import { useDomain } from '@/hooks/useDomain';
import { filterInboxTasksForLayers } from '@/lib/today/domainFilter';
import { useSupabaseTasks } from '@/hooks/useSupabaseTasks';
import { useRoutines } from '@/hooks/useRoutines';
import { useDiscussionInbox } from '@/hooks/useDiscussionInbox';
import { WaitingOnYouBanner } from '@/components/discussion/WaitingOnYouBanner';
import { useSymphonyAssistant, type AssistantTurnObserver } from '@/hooks/useSymphonyAssistant';
import { CanvasActivityProvider, useCanvasActivity, type CanvasActivity } from '@/contexts/CanvasActivityContext';
import { ConversationStrip } from '@/components/canvas/ConversationStrip';
import { ChatTaskActionsProvider } from '@/components/chat/ChatTaskActions';
import { conversationStripPath } from '@/lib/canvas/stripPaths';
import { ConversationWalkContext, type ConversationWalk } from '@/components/canvas/conversationWalk';
import { useScratchpadHidden } from '@/hooks/useScratchpadHidden';
import { useAssistantLaunchRequests, useAssistantLauncher } from '@/contexts/AssistantLaunchContext';
import { useShellChrome } from './useShellChrome';
import { addScratchpadNote, ownMemberId } from '@/lib/wall/addScratchpadNote';
import { SchoolPaperCapture } from '@/components/capture/SchoolPaperCapture';
import { localYmd } from '@/lib/cadence/config';
import { useSelection } from './providers/SelectionProvider';
import { MOBILE_TAB_BAR_HEIGHT } from './mobileChrome';
import { SideColumn, SIDE_COLUMN_WIDTH, type SidePane } from './SideColumn';
import { PhonePaneSwitch } from './PhonePaneSwitch';
import { NoteViewer } from '@/components/chat/NoteViewer';
import { useSceneryPreferences } from '@/hooks/useSceneryPreferences';
import { PlaceScenery } from '@/components/place/PlaceScenery';
import { planV2Enabled } from '@/lib/planning/v2/planV2';
import { onQuickAddRequest } from '@/lib/quickAddSignal';
import { useGlobalQuickAdd } from '@/desktop/useGlobalQuickAdd';
import { GuideProvider } from '@/hooks/useGuidedPlan';
import { PageGuideProvider, usePageGuide } from '@/components/explain/GuideProvider';
import { GuideLayer } from '@/components/explain/GuideLayer';
import { PageHelpContext } from '@/components/explain/PageHelp';
import { LearnPageContext } from '@/components/learn/LearnVideoChip';
import { EXPLAIN_LABEL, EXPLAIN_MESSAGE, hasPageGuide } from '@/lib/explain/pageGuides';
import { tourFor } from '@/lib/explain/pageTours';
import { requestShow } from '@/lib/explain/showSignal';

/**
 * ShellLayout wraps Shell-mounted apps with the Symphony app chrome — the
 * desktop page navigation, the mobile header + bottom nav, the QuickCapture FAB, the
 * domain switcher / AI / help top-bar buttons, pinned items, and the help
 * overlay.
 *
 * Why this exists: App.tsx (legacy mount) renders <AppShell> which wires up
 * the same chrome from App.tsx-local state. Shell-mounted apps bypass App.tsx
 * entirely. ShellLayout reproduces that chrome by sourcing data from the shared
 * hooks directly (see useShellChrome) rather than from props — so the chrome
 * works on every Shell route, including mobile (which previously rendered NO
 * chrome: no capture FAB, no bottom nav).
 *
 * Apps that should render full-bleed (kiosk surfaces like /wall) opt out by
 * setting `chromeless: true` on their AppDef — Shell.tsx skips this wrapper for
 * those apps, so the chrome here only ever wraps non-chromeless apps.
 *
 * NOTE on the right column: on desktop, Details and AI share ONE column
 * (SideColumn) on every page — one assistant, one conversation, and switching
 * panes keeps both mounted. Phones keep the full-screen detail panel and a
 * full-screen assistant overlay.
 */

// Left reference dock at its widest, and the narrowest page worth keeping
// beside it when a right-hand pane is also open.
const REFERENCE_DOCK_WIDTH = 340;
// The docked conversation bar at rest (ConversationStrip .is-docked): the
// landscape stands on it and side-by-side columns stop above it.

const MIN_PAGE_WITH_REFERENCES = 640;

function useViewportWidth() {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

/**
 * Derive ViewType from pathname so the Sidebar's active-item highlight
 * works for both legacy and Shell-mounted routes.
 */
export function deriveActiveView(pathname: string): ViewType {
  if (pathname.startsWith('/goals')) return 'goals';
  // /projects is hidden (2026-09-02) and redirects to /today in main.tsx, so
  // it never reaches here; see the note in Sidebar.tsx.
  if (pathname.startsWith('/routines')) return 'routines';
  if (pathname === '/contacts') return 'contacts';
  if (pathname.startsWith('/contacts/')) return 'contact-detail';
  if (pathname.startsWith('/meals')) return 'meals';
  if (pathname === '/inbox' || pathname.endsWith('/tasks-new/inbox')) return 'inbox';
  if (pathname.startsWith('/home')) return 'home-app';
  // /, /today, /tasks-new/today, /tasks-new and /task/:id all live under "today"
  return 'today';
}

/**
 * Pages that render their own domain/person filters and assistant entry in
 * their masthead. Every other page gets them in the desktop nav — previously
 * any path deriveActiveView didn't know (Someday, Notes, Lists, History…)
 * fell through to 'today' and silently lost the domain switcher, even though
 * Someday filters by it.
 */
const OWN_CHROME_PATHS = ['/', '/today', '/week', '/month', '/season', '/year', '/inbox', '/discussions', '/tasks-new'];
export function pageOwnsFilterChrome(pathname: string): boolean {
  return OWN_CHROME_PATHS.some((p) => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`)))
    || pathname.startsWith('/task/');
}

interface Props {
  children: ReactNode;
}

/**
 * Inner component — assumes <NotesProvider> + <ListsProvider> are mounted
 * (ShellLayout wraps the tree in both) so useShellChrome's note/list reads work.
 */
function ShellLayoutInner({ children }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const destination = defaultPlanningDestination(location.pathname, location.search);
    if (destination) navigate(destination + location.hash, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);
  const connectedView = new URLSearchParams(location.search).get('view');
  const connectedWorkspace = connectedView === 'alongside' || connectedView === 'constellation' || new URLSearchParams(location.search).get('workspace') === '1';
  // Planning v2 dresses the whole shell (headings, the day numeral) while it
  // is on for this device (docs/planning/2026-09-28-planning-v2.md).
  useEffect(() => { document.documentElement.classList.toggle('plan-v2', planV2Enabled()); }, [location.search]);
  const isMobile = useMobile();
  const typing = useTextEntryActive();
  const { user, signOut } = useAuth();
  // Module caches that are not components — the planning calendar behind the
  // day tiles — key their data on who is signed in. The shell is the one
  // place that knows, once.
  useEffect(() => { setCurrentAccount(user?.id ?? null); }, [user?.id]);
  // The household's week start (Saturday for a Friday planning session), for
  // every device in it — read once here, mirrored into the cadence config.
  useHouseholdWeekStart();
  const {config: cadenceConfig} = useCadenceConfig();

  const [desktopControls, setDesktopControls] = useState<HTMLDivElement | null>(null);
  const [desktopLead, setDesktopLead] = useState<HTMLDivElement | null>(null);
  const [desktopCenter, setDesktopCenter] = useState<HTMLDivElement | null>(null);
  const [mobilePlanControls, setMobilePlanControls] = useState<HTMLDivElement | null>(null);
  // The element that scrolls the page — the content frame, on desktop and
  // phone alike. Focused controls must clear the scenery foreground.
  const [pageScroller, setPageScroller] = useState<HTMLDivElement | null>(null);
  const { showScenery, sceneryLighting } = useSceneryPreferences();
  // Today, Week, Plan and Routines on desktop have one conversation: the bar
  // docked at the window's foot (approved boards, 2026-10-10). The side pane
  // is for Details there.
  const dockedBar = !isMobile && conversationStripPath(location.pathname);
  // Plan's docked bar is the desktop floor the landscape stands on.
  const sceneryFloor = isMobile ? `calc(${MOBILE_TAB_BAR_HEIGHT} + env(safe-area-inset-bottom, 0px))` : undefined;

  const references = useReferenceLists();

  const reviewedWriter = useMemo(() => createReviewedWriter(), []);
  // Chat's choice ops write through the reviewed client too, so the card's
  // save receipt journals them and its Undo reverses them.
  const userId = user?.id;
  const choiceWriters = useMemo(() => (userId ? makeChoiceWriters(reviewedWriter.client, userId) : undefined), [reviewedWriter, userId]);
  const { tasks, refetch, deleteTask, updateTask, addTask, completeTask, toggleTask, keepForward, dropCommitment } = useSupabaseTasks(reviewedWriter.client);
  const { goals, deleteGoal, updateGoal, addGoal } = useGoals(reviewedWriter.client);
  // Held so routine writes from a conversation are seen (and undoable) too.
  const { routines: canvasRoutines, deleteRoutine, updateRoutine, addRoutine, refetch: refetchRoutines } = useRoutines(reviewedWriter.client);
  // The change card's this-week routine ops write one day's occurrence.
  const { skip: skipInstance, undoDone: undoInstanceDone, reschedule, undoReschedule, setOccurrenceAssignee } = useActionableInstances(reviewedWriter.client);
  // The canvas activity provider sits inside this component's tree, but the
  // assistant hook runs above it: a ref bridges each turn's events across.
  const canvasRef = useRef<CanvasActivity | null>(null);
  const setCanvasActivity = useCallback((activity: CanvasActivity) => { canvasRef.current = activity; }, []);
  const turnObserver = useMemo<AssistantTurnObserver>(() => ({
    start: ({ text, retry }) => canvasRef.current?.turnStarted(text, retry),
    tool: (name) => canvasRef.current?.toolUsed(name),
    toolResult: (result) => canvasRef.current?.toolResult(result),
    proposal: (items) => canvasRef.current?.addProposals(items),
    changeSet: (set) => canvasRef.current?.setChangeSet(set),
    point: (p) => canvasRef.current?.setPointing(p),
    end: (turn) => canvasRef.current?.turnEnded(turn),
    changeCard: () => canvasRef.current?.takeChangeCard(),
  }), []);
  const [planningPeople] = useAssigneeFilter();
  const scopedTasks = useMemo(()=>tasks.filter(planPeopleLens(planningPeople,null).keep),[tasks,planningPeople]);
  const scopedGoals = useMemo(()=>goals.filter(planPeopleLens(planningPeople,null).keep),[goals,planningPeople]);
  // The count beside Inbox is the discussions waiting on you (2026-10-14).
  const { waitingCount: discussionsUnread, rows: discussionRows } = useDiscussionInbox({ defer: true });
  // The badge MUST mirror what the Inbox actually renders. It used to count
  // every inbox task regardless of the active domain layers, so an item in an
  // unchecked layer showed as "1 to triage" in the chrome while the Inbox —
  // and Focus mode — said "Inbox zero" (launch rehearsal, 2026-09-04). It
  // then drifted the other way when the Inbox began always showing untagged
  // captures; both now use the one selector.
  const { layers, soleDomain } = useDomain();
  const inboxCount = useMemo(
    () => filterInboxTasksForLayers(tasks, layers).filter((t) => t.bucket === 'inbox' && !t.completed).length,
    [tasks, layers],
  );

  // Chrome data + handlers, sourced from shared hooks (not props).
  const chrome = useShellChrome();

  // The selected item opens Details in the right column.
  const { selection, clearSelection } = useSelection();

  // Mobile/UI chrome state
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  // A school paper photographed onto the wall (spec 2026-10-20): who took it.
  const [schoolPaperHolder, setSchoolPaperHolder] = useState<string | null>(null);
  // Following a capture's "Go to inbox" (or any link) left Quick Add open
  // over the page it had just sent you to. Going somewhere closes it.
  const [quickAddPath, setQuickAddPath] = useState(location.pathname);
  if (quickAddPath !== location.pathname) {
    setQuickAddPath(location.pathname);
    if (quickAddOpen) setQuickAddOpen(false);
  }
  // "Add task" from the Planning panel (dock or sheet) asks the shell to open
  // the same unibox ⌘K opens — one add box, wherever it is asked for.
  useEffect(() => onQuickAddRequest(() => setQuickAddOpen(true)), []);

  // ⌃⌥Space from any app (Mac shell) opens this same unibox and hands focus
  // back when it closes (useGlobalQuickAdd).
  const { closeQuickAdd, stayInSymphony, tracked } = useGlobalQuickAdd(quickAddOpen, setQuickAddOpen);

  // Global keyboard shortcuts: ⌘K opens the unibox (Quick Add + search + Ask
  // Symphony); ⌘/ is a legacy alias. Both work anywhere.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      const key = e.key.toLowerCase();
      // ⌘⇧K is Talk to Symphony (below), not the box.
      if ((key === 'k' && !e.shiftKey) || key === '/') {
        e.preventDefault();
        setQuickAddOpen((o) => !o);

      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // One assistant for the whole shell: the desktop column's AI pane and the
  // phone overlay show the same conversation. On desktop its visibility is
  // the persisted rail preference (useScratchpadHidden — the masthead's AI
  // button toggles the same state); phones open it per launch.
  const conversationContext = useMemo(() => assistantSelection(scopedTasks, selection, layers), [scopedTasks, selection, layers]);
  const screenContext = useMemo(() => workspaceContext(scopedTasks, location.pathname, location.search, layers, selection?.kind === 'task' ? selection.id : null, new Date(), scopedGoals), [scopedTasks, scopedGoals, location.pathname, location.search, layers, selection, cadenceConfig.weekStartsOn]);
  const approveConversationCard = async () => { const a = canvasRef.current; if (!a?.changeSet) return 'none' as const; let outcome: 'saved' | 'partial' | 'failed' = 'failed'; await a.approveChangeSet(state => { outcome = state; }); return outcome; };
  const assistant = useSymphonyAssistant({ resolveAction: text => resolveConversationAction(text, approveConversationCard, async () => (await canvasRef.current?.undo()) ?? 'none'), persistKey: 'symphony_rail', onPlanSaved: (saved) => {
    void refetch();
    window.dispatchEvent(new Event('symphony-plan-updated'));
    if (connectedWorkspace && !document.querySelector('dialog[open]') && Number.isInteger(saved.level) && saved.level >= 0 && saved.level <= 3 && /^\d{4}-\d{2}-\d{2}$/.test(saved.date)) {
      const query = new URLSearchParams({ view: 'constellation', start: saved.date, horizon: String(saved.level), focus: `${saved.level}:${saved.id}` });
      navigate(`/year?${query}`, { replace: true });
    }
  }, onWorkspace: (page,date) => { if (connectedWorkspace) { const url=workspaceDestination(page,date); if(url) navigate(url); } }, taskContext: conversationContext.taskContext, workspaceContext: connectedWorkspace ? screenContext : undefined, onMutate: () => { void refetch(); window.dispatchEvent(new Event('symphony-plan-updated')); }, turnObserver });
  const { hidden: aiHidden, setHidden: setAiHidden } = useScratchpadHidden();
  const [phoneChatOpen, setPhoneChatOpen] = useState(false);
  const [conversationDraft, setConversationDraft] = useState('');
  const [pane, setPane] = useState<SidePane>('details');
  // Talk is deferred for beta; retain the transport for a later voice pass.
  const workspaceTalkEnabled = false;
  const voiceAccess = useWorkspaceVoiceAccess(user?.id ?? null, connectedWorkspace && workspaceTalkEnabled);
  const voiceEnabled = workspaceTalkEnabled && connectedWorkspace && voiceAccess;
  const voice = useWorkspaceVoice(screenContext, assistant.sendTurn, (url) => navigate(import.meta.env.DEV && new URLSearchParams(location.search).get('voice') === '1' ? `${url}&voice=1` : url), voiceEnabled, user?.id ?? null, async () => { const a = canvasRef.current; if (!a?.changeSet) return 'none'; let outcome: 'saved' | 'partial' | 'failed' = 'failed'; await a.approveChangeSet(state => { outcome = state; }); return outcome; }, async () => (await canvasRef.current?.undo()) ?? 'none');
  // Talk to Symphony from anywhere (Scott, 2026-10-14: one front door — ⌘K
  // adds, searches, asks, or talks). Starting closes the box; the conversation
  // carries on in the bar at the page's foot. Already talking: your turn.
  const startTalking = useCallback(() => {
    if (!voiceEnabled) return;
    setQuickAddOpen(false);
    stayInSymphony();
    if (isMobile) setPhoneChatOpen(true); else { setAiHidden(false); setPane('ai'); }
    if (voice.active) voice.talk();
    else void voice.start();
  }, [voiceEnabled, voice, stayInSymphony, isMobile, setAiHidden]);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        startTalking();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [startTalking]);
  // Phone: a long press on the dock's + talks instead of adding.
  const addPress = useRef<{ timer: ReturnType<typeof setTimeout> | null; fired: boolean }>({ timer: null, fired: false });
  const addPressHandlers = voiceEnabled ? {
    onPointerDown: () => {
      addPress.current.fired = false;
      addPress.current.timer = setTimeout(() => { addPress.current.fired = true; startTalking(); }, 500);
    },
    onPointerUp: () => { if (addPress.current.timer) clearTimeout(addPress.current.timer); },
    onPointerLeave: () => { if (addPress.current.timer) clearTimeout(addPress.current.timer); },
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  } : {};
  const pageGuide = usePageGuide();
  const sendConversation = (text: string, attachment?: Parameters<typeof assistant.sendMessage>[1]) => {
    // What Symphony showed belongs to the last exchange.
    pageGuide?.close();
    if (isMobile) setPhoneChatOpen(true); else showPane('ai');
    // The voice transport now delegates typed and spoken turns to this same
    // assistant. Stop playback before falling back to a text-only turn.
    if (voiceEnabled && !attachment && voice.active && voice.sendText(text)) return;
    voice.stop();
    void assistant.sendMessage(text, attachment);
  };
  const resetConversation = () => { voice.stop(); setConversationDraft(''); assistant.resetSession(); };

  const [walk, setWalk] = useState<ConversationWalk | null>(null);
  // "What is this page?" beside the walk, on pages Symphony can explain.
  // It plays the page's own tour (pageTours.ts); Symphony answers only if the
  // page has none on screen.
  const explain = connectedWorkspace && hasPageGuide(screenContext.page) ? {
    label: EXPLAIN_LABEL, message: EXPLAIN_MESSAGE,
    show: () => { const steps = tourFor(screenContext.page); if (!steps.length) return false; requestShow(steps, 'chat'); return true },
  } : null;
  // The page shows it as a "?" beside its title (PageHelp.tsx).
  const pageHelp = explain ? () => { if (!explain.show()) sendConversation(explain.message) } : null;
  const phoneChatRef = useRef<HTMLDivElement>(null);
  useDialogFocus(phoneChatOpen && isMobile, phoneChatRef, () => setPhoneChatOpen(false));
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const aiOpen = !isMobile && !aiHidden;
  // A new selection brings Details to the front.
  const selectionKey = selection ? `${selection.kind}:${selection.id}` : null;
  useEffect(() => { if (selectionKey) setPane('details'); }, [selectionKey]);
  const showPane = useCallback((next: SidePane) => {
    if (next === 'ai') setAiHidden(false);
    setPane(next);
  }, [setAiHidden]);

  // Programmatic launches (unibox "Ask Symphony", Add-to-today…).
  const { openAssistant } = useAssistantLauncher();
  const { nonce: launchNonce, consumeSeed } = useAssistantLaunchRequests();
  const seenLaunchNonce = useRef(0);
  useEffect(() => {
    if (launchNonce === 0 || launchNonce === seenLaunchNonce.current) return;
    seenLaunchNonce.current = launchNonce;
    const seed = consumeSeed();
    if (seed?.stayOnPage) { pageGuide?.close(); }
    else if (isMobile) setPhoneChatOpen(true);
    else showPane('ai');
    if (seed && seed.autoSend !== false) void assistant.sendMessage(seed.message);
  }, [launchNonce, isMobile, dockedBar, consumeSeed, assistant, showPane, pageGuide]);

  // Closing the More sheet returns focus to its tab, not to <body>.
  const moreTabRef = useRef<HTMLButtonElement>(null);
  const closeMoreSheet = useCallback(() => {
    setMoreSheetOpen(false);
    moreTabRef.current?.focus();
  }, []);

  // Pinned lists sit on the LEFT, so they no longer compete with the right
  // column: both stay open while the page keeps a readable width between
  // them. Only when it would not do the column still wins, and the lists
  // return when it closes. The dock column is reserved only when a pin
  // actually draws here, so the page centres in the width left.
  const viewportWidth = useViewportWidth();
  const columnOpen = !isMobile && (!!selection || aiOpen);
  const paneWidth = columnOpen ? SIDE_COLUMN_WIDTH : 0;
  const referencesFit = paneWidth === 0 || viewportWidth - paneWidth - REFERENCE_DOCK_WIDTH >= MIN_PAGE_WITH_REFERENCES;
  const referencesPaused = paneWidth > 0 && !referencesFit;
  const referencesVisible = !isMobile && referencesFit
    && !!references?.pins.some((pin) => !pinIsOnPage(location.pathname, pin.kind));

  const canvasSnapshot = useMemo(() => ({ tasks, goals, routines: canvasRoutines }), [tasks, goals, canvasRoutines]);
  const canvasWriters = useMemo(() => ({
    deleteTask, updateTask, deleteGoal,
    updateGoal: updateGoal as (id: string, u: Partial<import('@/types/goal').Goal>) => Promise<unknown>,
    deleteRoutine,
    updateRoutine: updateRoutine as unknown as (id: string, u: Partial<import('@/types/routine').Routine>) => Promise<unknown>,
  }), [deleteTask, updateTask, deleteGoal, updateGoal, deleteRoutine, updateRoutine]);
  // Undo reads the tasks as they are THEN, not as they were at Approve: a
  // task already reopened by hand must not be toggled closed again.
  const latestTasks = useRef(tasks);
  useEffect(() => { latestTasks.current = tasks; }, [tasks]);
  const applyChanges = useCallback((set: ChangeSet) => applyReviewedChange(set, {
    tasks, updateTask, deleteTask,
    addTask: (title, when, options) => addTask(title, undefined, undefined, when, options),
    weekStart: plannedWeekStart(set, new Date(), readCadenceConfig().weekStartsOn),
    completeTask,
    reopenTask: async (id) => {
      const now = withSteps(latestTasks.current).find((t) => t.id === id);
      return now && !now.completed ? true : toggleTask(id);
    },
    routines: canvasRoutines, updateRoutine, deleteRoutine,
    addRoutine: async (input) => (await addRoutine(input))?.id,
    skipOccurrence: (id, date) => skipInstance('routine', id, date),
    undoSkip: (id, date) => undoInstanceDone('routine', id, date),
    moveOccurrence: (id, from, to, opts) => reschedule('routine', id, from, to, opts),
    undoMove: undoReschedule,
    setOccurrenceAssignee: (id, date, memberId) => setOccurrenceAssignee('routine', id, date, memberId),
    // Month / Season / Year: the pages' own writers (ConstellationPage, the year carry).
    defaultContext: soleDomain ?? undefined,
    goals,
    addGoal: async (name, { year, context, carriedFrom, areaId, notes, strategy, assignedToAll }) =>
      (await addGoal(areaId ?? null, name, context, { year, carriedFrom, notes, strategy, assignedToAll }))?.id,
    updateGoal, deleteGoal, keepForward, dropCommitment,
    choiceWriters,
    // Undo's conflict check reads the rows as they are then.
    latest: () => ({ tasks: latestTasks.current, routines: canvasRoutines }),
  }, reviewedWriter), [reviewedWriter, tasks, updateTask, deleteTask, addTask, completeTask, toggleTask, canvasRoutines, updateRoutine, deleteRoutine, addRoutine, skipInstance, undoInstanceDone, reschedule, undoReschedule, setOccurrenceAssignee, soleDomain, goals, addGoal, updateGoal, deleteGoal, keepForward, dropCommitment, choiceWriters]);
  const canvasRefetch = useCallback(() => { window.dispatchEvent(new Event('symphony-plan-updated')); return Promise.all([refetch(), refetchRoutines()]); }, [refetch, refetchRoutines]);
  // School review saves happen in Settings. Refresh the mounted planning
  // data immediately, including when the realtime socket has gone to sleep.
  useEffect(() => {
    const refresh = () => { void canvasRefetch(); };
    window.addEventListener('symphony-school-review-saved', refresh);
    return () => window.removeEventListener('symphony-school-review-saved', refresh);
  }, [canvasRefetch]);
  const showStrip = conversationStripPath(location.pathname);
  const stripVoice = { available: voiceEnabled, active: voiceEnabled && voice.active, status: voice.status, error: voice.error, speaking: voice.speaking, working: voice.working, start: () => { void voice.start(); }, stop: voice.stop, heard: voice.heard, reply: voice.reply, turn: voice.turn, talk: voice.talk, done: voice.done };
  // One chat pane on every page, desktop and phone (Scott, 2026-10-24: the
  // planning pages had "Chat" and the rest an older "Symphony AI" pane, so the
  // same conversation looked like two assistants). Its history, files and
  // note links came over from the older pane.
  const chatPane = (onClose?: () => void) => <ConversationStrip chatOnly panel draft={conversationDraft} onDraftChange={setConversationDraft}
    error={assistant.error} messages={assistant.messages} walk={conversationStripPath(location.pathname) ? walk : null}
    onSend={sendConversation} busy={assistant.loading} voice={stripVoice} onClose={onClose}
    history={{ sessions: assistant.sessions, loading: assistant.sessionsLoading, activeSessionId: assistant.activeSessionId,
      onLoad: (session) => { voice.stop(); assistant.loadSession(session); }, onDelete: assistant.deleteSession, onNew: resetConversation }}
    toolActivity={assistant.toolActivity} onSourceClick={setActiveNoteId}
    emptyHint={conversationContext.entityContext ? `Ask about ${conversationContext.entityContext.name}` : undefined} />;
  const openConversation = () => { if (isMobile) setPhoneChatOpen(true); else showPane('ai'); };

  return (
    <CanvasActivityProvider snapshot={canvasSnapshot} writers={canvasWriters} refetch={canvasRefetch} applyChanges={applyChanges} persistKey={user?.id} retryTurn={() => { void assistant.retryLast(); }}>
    <ChatTaskActionsProvider tasks={tasks} toggleTask={toggleTask} updateTask={updateTask} routines={canvasRoutines}>
    <PageHelpContext.Provider value={pageHelp}>
    <LearnPageContext.Provider value={screenContext.page}>
    <CanvasBridge onActivity={setCanvasActivity} />
    <DesktopControlsContext.Provider value={desktopControls}>
    <DesktopLeadContext.Provider value={desktopLead}>
    <DesktopCenterContext.Provider value={desktopCenter}>
    <MobilePlanControlsContext.Provider value={mobilePlanControls}>
    <ConversationWalkContext.Provider value={dockedBar ? setWalk : null}>
    {/* With scenery on, the shell wears the place's sky in the chosen light
        (index.css, IMMERSIVE SCENERY); page regions marked .scenery-page
        take its text colours. */}
    <div
      className={`${connectedWorkspace ? 'connected-workspace ' : ''}${showScenery ? 'scenery-sky ' : ''}h-screen flex overflow-hidden overflow-x-hidden bg-bg-base w-full max-w-[100vw]`}
      data-scenery-lighting={showScenery ? sceneryLighting : undefined}
    >
      {/* "New version available — reload" banner: shows when a newer build
          deployed while this tab stayed open (stale-tab guard). */}
      <NewVersionBanner />
      {voiceEnabled && voice.active && !(isMobile ? phoneChatOpen : aiOpen && pane === 'ai') && <div className="workspace-voice-floating" role="status"><span className="workspace-voice-floating-words">{lastWords(voice.turn === 'listening' ? (voice.heard || 'Listening — take your time') : voice.reply || voice.heard || 'Voice on')}</span>{voice.status === 'live' && (voice.turn === 'listening' ? <button onClick={voice.done}>Done</button> : <button onClick={voice.talk}>Talk</button>)}<button onClick={voice.stop}>End voice</button></div>}

      {/* Content frame — uses <div> (not <main>) because individual apps render
          their own <main>. Avoids invalid nested-main HTML. */}
      <div
        ref={setPageScroller}
        data-explain-root
        inert={phoneChatOpen && isMobile}
        className={`${showScenery ? 'scenery-scroll' : ''} relative flex-1 overflow-auto overflow-x-hidden ${isMobile ? '' : 'transition-all duration-300 ease-in-out'}`}
        style={
          isMobile
            ? {
                // Must clear the fixed bottom tab bar below (MOBILE_TAB_BAR_HEIGHT),
                // plus a little slack, plus the safe-area inset the bar itself sits
                // above. See mobileChrome.ts — this used to be a separate, smaller
                // hardcoded literal (2.75rem) that silently fell out of sync with
                // the bar's actual height and clipped AttentionLine.
                paddingBottom: `calc(${MOBILE_TAB_BAR_HEIGHT} + 0.5rem + env(safe-area-inset-bottom, 0px))`,
              }
            : { marginRight: `${paneWidth}px`,  } as CSSProperties
        }
      >
        {/* Mobile header — domain switcher (date nav lives in
            HomeHeader on Today). This header is the one piece of chrome every
            mobile Shell route renders, so the switcher lives here and nowhere
            else on phones: HomeHeader hides its copy below md, and the desktop
            top-right cluster further down is gated on !isMobile. Sign out is
            NOT here: an unlabelled one-tap door on the page people live in
            (walkthrough 2026-09-21, C-P1) — it lives in Settings, with a
            confirm. */}
        {/* Phone Today folds the domain lens into its one Filters control in
            the tab row, so this header row would only repeat it. */}
        {isMobile && !planPeriodForPath(location.pathname) && (
          // Rides in the top-right corner, level with the page title, rather
          // than spending a row of its own above it.
          <header
            className="phone-page-lens scenery-page absolute right-0 top-0 z-10 flex items-center gap-1 px-4 pt-2"
            style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
          >
            <HeaderPeopleFilter />
            <DomainSwitcher />
          </header>
        )}

        {isMobile ? (
          <div className="scenery-page">
            <PhoneBack isRoot={location.pathname === HOME_ROUTE || !!planPeriodForPath(location.pathname)} />
            <PlanNavigation mobile mobileControlsRef={setMobilePlanControls} />
            {location.pathname !== '/discussions' && (
              <WaitingOnYouBanner rows={discussionRows}
                onOpen={(row) => navigate(row ? `/discussions?open=${row.sessionId}` : '/discussions')} />
            )}
            <div className="min-w-0">{children}</div>
            {showStrip && <ConversationStrip chatOnly compact receiptOnly onSend={(text) => sendConversation(text)} busy={assistant.loading} voice={stripVoice} onOpenConversation={openConversation} />}
          </div>
        ) : (
          // Desktop: navigation and page share one centred column —
          // centred in the window, or in the space left beside a side pane or
          // the pinned reference lists.
          <>
          <div className={`desktop-workspace relative${referencesVisible ? ' has-references' : ''}`}>
            <div className="desktop-workspace-nav scenery-page">
        <DesktopNavigation inboxCount={inboxCount} discussionsUnread={discussionsUnread}
          onSearch={() => setQuickAddOpen(true)} onSignOut={signOut}
          userName={user?.user_metadata?.name ?? user?.email}
          paused={referencesPaused} controlsRef={setDesktopControls} leadRef={setDesktopLead} centerRef={setDesktopCenter}
          // Area and assistant live in the top bar on EVERY page (Scott,
          // 2026-09-30) — beside Inbox, search and you; pages don't repeat them.
          auxiliaryControls={(
          // Plain icons like Inbox, search and you — no circles (Scott,
          // 2026-09-30); one even gap across the group.
          <div className="contents">
            <HeaderPeopleFilter />
            <DomainSwitcher />
            {/* On Plan the conversation is the bar at the foot. */}
            {<button
              onClick={() => { setAiHidden(aiOpen); setPane('ai'); }}
              className={`page-navigation-icon${aiOpen ? ' is-on' : ''}`}
              aria-label="Chat"
              aria-pressed={aiOpen}
              title="Chat"
            >
              <Sparkles className="w-[17px] h-[17px]" aria-hidden="true" />
            </button>}
          </div>
        )} />
            </div>
            <div className="desktop-workspace-page min-w-0"><PlanNavigation paused={referencesPaused} />
              <SideColumn
                hasSelection={!!selection}
                aiOpen={aiOpen}
                aiLabel="Chat"
                pane={pane}
                onPaneChange={showPane}
                onCloseDetails={clearSelection}
                onCloseAi={() => setAiHidden(true)}
                ai={chatPane()}
              >
                <div className={`scenery-page scenery-page-contents`}>
                  {children}
                  {dockedBar
                    ? !aiOpen && <button type="button" className="conversation-launcher" style={{ right: paneWidth + 24 }} onClick={openConversation}><Sparkles size={18} aria-hidden="true" />Chat with Symphony</button>
                    : showStrip && <ConversationStrip chatOnly onSend={(text) => sendConversation(text)} busy={assistant.loading} voice={stripVoice} onOpenConversation={openConversation} />}
                </div>
              </SideColumn>
            </div>
            {referencesVisible && <div className="desktop-workspace-dock"><ReferenceListsDock /></div>}
            {/* No footer bar (Scott, 2026-10-02): the landscape at the foot of
                the page stays clear. Shortcuts and Help are in the ☰ menu;
                Today's review closes its own list. */}
          </div>
          </>
        )}
        {showScenery && isMobile && <div className="scenery-end-clearance" aria-hidden="true" />}
        <PlaceScenery scroller={pageScroller} right={isMobile ? 0 : paneWidth} floor={sceneryFloor} vivid={location.pathname === HOME_ROUTE} />
      </div>

      {/* QuickCapture FAB — every route */}
      {(
        <QuickCapture
          // Desktop keeps its round + at the bottom right (Scott, 2026-09-26:
          // "desktop has ALWAYS had a round circle + button" — it did until
          // the 2026-09-18 top-navigation redesign hid it). It opens the same
          // ⌘K add box as the shortcut. Phones add
          // through the dock's + (and the capture bar) instead.
          showFab={!isMobile}
          rightInset={isMobile ? 0 : paneWidth}
          onAdd={tracked(chrome.onQuickAdd)}
          onAddRich={tracked(chrome.onQuickAddRich)}
          // useShellChrome confirms every capture once its write lands.
          confirmsAfterWrite
          onAddNote={tracked(chrome.onQuickAddNote)}
          eventCalendarName={chrome.eventCalendarName}
          onPlanFromPaper={() => { stayInSymphony(); if (!requestPlanFromPaper()) navigate('/today') }}
          onSchoolPaper={user ? () => { void ownMemberId(user.id).then((id) => setSchoolPaperHolder(id ?? '')) } : undefined}
          projects={chrome.quickAddProjects}
          contacts={chrome.quickAddContacts}
          familyMembers={chrome.quickAddFamilyMembers}
          isOpen={quickAddOpen}
          onOpen={() => setQuickAddOpen(true)}
          onClose={closeQuickAdd}
          resultsSlot={(query, close) => <OmniboxResults query={query} onNavigate={() => { stayInSymphony(); close() }} />}
          onAskSymphony={(text) => { stayInSymphony(); openAssistant({ message: text, autoSend: true }) }}
          onTalk={voiceEnabled ? startTalking : undefined}
          onAddToScratchpad={user ? async (text, kind) => addScratchpadNote(user.id, text, kind, await ownMemberId(user.id)) : undefined}
          onAddCaptureEvents={chrome.onAddCaptureEvents}
        />
      )}

      {/* Mobile AI rail — full-screen overlay */}
      {isMobile && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-bg-elevated"
          ref={phoneChatRef}
          role="dialog"
          aria-modal="true"
          aria-label="Symphony conversation"
          onKeyDown={event => {
            if (event.key !== 'Tab') return;
            const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex="0"]')).filter(el => el.getClientRects().length > 0);
            const first = controls[0], last = controls.at(-1);
            if (!first || !last) return;
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
          }}
          // Taps in here are not a tap "outside" the Details panel beneath.
          data-panel-keepalive
          style={{
            display: phoneChatOpen ? undefined : 'none',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
        >
          {/* With an item open, the same Details | AI switch as desktop:
              Details is still mounted underneath, edits and all. */}
          {selection && <PhonePaneSwitch active="ai" onChange={(p) => { if (p === 'details') setPhoneChatOpen(false); }} />}
          <div className="min-h-0 flex-1">
          {chatPane(() => setPhoneChatOpen(false))}
          </div>
        </div>
      )}

      {/* The native dock: Home · Today · + · Plan · More. Home is where you
          come back to for your bearings (2026-10-11); Plan opens the week, and
          its title menu (and Home's "Where you are") reach Month, Season and
          Year. While a text field has focus the keyboard owns the bottom
          edge, so the dock steps aside and the capture bar sits on the
          keyboard (as on iOS). */}
      {isMobile && !typing && (
        <nav className="phone-dock scenery-page" aria-label="Main">
          <div className="phone-dock-row">
            <button type="button" className="phone-dock-tab"
              onClick={() => navigate(connectedDestination(HOME_ROUTE, ''))}
              aria-current={location.pathname === HOME_ROUTE ? 'page' : undefined}>
              <HomeIcon aria-hidden="true" />
              <span>Home</span>
            </button>
            <button type="button" className="phone-dock-tab"
              onClick={() => navigate(connectedDestination('/today', ''))}
              aria-current={planPeriodForPath(location.pathname) === 'today' ? 'page' : undefined}>
              <Sun aria-hidden="true" />
              <span>Today</span>
            </button>
            <div className="phone-dock-add-slot">
              <button type="button" className="phone-dock-add" {...addPressHandlers}
                onClick={() => { if (addPress.current.fired) { addPress.current.fired = false; return; } setQuickAddOpen(true); }}
                aria-label={voiceEnabled ? 'Add (press and hold to talk to Symphony)' : 'Add'}>
                <Plus aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="phone-dock-tab"
              onClick={() => navigate(connectedDestination('/week', ''))}
              aria-current={['week', 'month', 'season', 'year'].includes(planPeriodForPath(location.pathname) ?? '') ? 'page' : undefined}
            >
              <Map aria-hidden="true" />
              <span>Plan</span>
            </button>
            <button
              type="button"
              ref={moreTabRef}
              className="phone-dock-tab"
              onClick={() => setMoreSheetOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreSheetOpen}
              aria-label={`More${inboxCount ? `, ${inboxCount} in Inbox` : ''}`}
            >
              <MoreHorizontal aria-hidden="true" />
              <span>More</span>
              {inboxCount > 0 && (
                <span className="phone-dock-badge" aria-hidden="true">{inboxCount > 99 ? '99+' : inboxCount}</span>
              )}
            </button>
          </div>
        </nav>
      )}

      {/* MoreSheet — mobile slide-up menu */}
      {isMobile && (
        <MoreSheet
          isOpen={moreSheetOpen}
          onClose={closeMoreSheet}
          discussionsUnread={discussionsUnread}
          inboxCount={inboxCount}
          // Opens the conversation without sending anything.
          onAskSymphony={() => { setMoreSheetOpen(false); setPhoneChatOpen(true); }}
        />
      )}

      {activeNoteId && (
        <NoteViewer key={activeNoteId} noteId={activeNoteId} onClose={() => setActiveNoteId(null)} />
      )}

      {/* Always mounted, so toast text is announced to screen readers. */}
      <ToastLiveRegion
        message={chrome.toast?.message ?? chrome.confirmationToast?.message}
        urgent={chrome.toast?.type === 'error'}
      />

      {/* Toast — surfaces QuickCapture note-save feedback */}
      {chrome.toast && (
        <Toast toast={chrome.toast} onDismiss={chrome.dismissToast} />
      )}

      {/* Capture confirmation — "Added to Inbox" with one-tap Today/Tomorrow */}
      {chrome.confirmationToast && (
        <ConfirmationToast
          toast={chrome.confirmationToast}
          onDismiss={chrome.dismissConfirmationToast}
        />
      )}
      {schoolPaperHolder !== null && user && (
        <SchoolPaperCapture
          userId={user.id}
          holderId={schoolPaperHolder}
          kids={chrome.familyKids}
          today={localYmd(new Date())}
          onClose={() => setSchoolPaperHolder(null)}
        />
      )}
    </div>
    </ConversationWalkContext.Provider>
    </MobilePlanControlsContext.Provider>
    </DesktopCenterContext.Provider>
    </DesktopLeadContext.Provider>
    </DesktopControlsContext.Provider>
    </LearnPageContext.Provider>
    </PageHelpContext.Provider>
    </ChatTaskActionsProvider>
    </CanvasActivityProvider>
  );
}

/** Hands the canvas activity to code that runs above its provider. */
function CanvasBridge({ onActivity }: { onActivity: (activity: CanvasActivity) => void }) {
  const activity = useCanvasActivity();
  useLayoutEffect(() => { onActivity(activity); }, [onActivity, activity]);
  return null;
}

/** One line of a long sentence: its latest words (what was just said). */
const lastWords = (text: string, max = 80) => (text.length > max ? `…${text.slice(-max).replace(/^\S*\s/, '')}` : text);

export function ShellLayout({ children }: Props) {
  const { user } = useAuth();
  // Wrap the chrome's data needs (note quick-add, pinned lists) in the same
  // contexts the legacy AppShell relied on. Apps that render their own copies
  // (e.g. TasksApp) nest harmlessly inside these.
  return (
    <ListsProvider>
      <NotesProvider deferLoad>
        <PinsProvider>
          <ReferenceListsProvider key={user?.id ?? "anonymous"} userId={user?.id ?? "anonymous"}><GuideProvider><PageGuideProvider><ShellLayoutInner>{children}</ShellLayoutInner><GuideLayer /></PageGuideProvider></GuideProvider></ReferenceListsProvider>
        </PinsProvider>
      </NotesProvider>
    </ListsProvider>
  );
}
